/**
 * The exact SDL each instance was signed with, kept in this browser.
 *
 * "Start workload" has to send the provider a manifest whose hash matches the
 * one on chain. It used to rebuild that manifest from the guided draft, which
 * only matches for an instance created from that same draft: an instance
 * written in the advanced SDL editor, or a guided draft edited after renting,
 * always failed the hash check. Keying the signed SDL by owner and dseq makes
 * the resend independent of whichever draft the browser holds now.
 *
 * Browser storage can be unavailable (embedded wallets, private windows), so
 * every access is guarded and a miss falls back to the caller's old path.
 */

import { sdlBuildImages, type BuildInput } from './imageBuild'
import { SdlParseError } from './sdl'

function key(owner: string, dseq: bigint | number | string): string {
  return `ddws.console.deployed-sdl.${owner}.${dseq.toString()}`
}

/** Remember `sdl` as the configuration `owner` signed for `dseq`. */
export function rememberDeployedSdl(owner: string, dseq: bigint | number | string, sdl: string, buildInput?: BuildInput): void {
  let images: string[] = []
  try { images = sdlBuildImages(sdl) } catch (cause) {
    // Legacy snapshots may be opaque text. Every billing path validates the
    // SDL separately; valid YAML image aliases/escapes are always inspected.
    if (!(cause instanceof SdlParseError)) throw cause
  }
  const building = images.length > 0
  if (building && (!buildInput || images.some((image) => image !== buildInput.image))) throw new Error('The prepared Dockerfile inputs do not match this configuration. Return to Configure and prepare them before renting.')
  try {
    window.localStorage.setItem(key(owner, dseq), sdl)
    if (building) window.localStorage.setItem(`ddws.console.deployed-build.${owner}.${dseq}`, JSON.stringify(buildInput))
  } catch {
    if (building) throw new Error('The browser could not save the prepared Dockerfile inputs. Enable browser storage before renting.')
    /* Image workloads can reconstruct their manifest from the draft. */
  }
}

/** The SDL `owner` signed for `dseq` in this browser, or null if it was not recorded here. */
export function deployedSdl(owner: string, dseq: bigint | number | string): string | null {
  try { return window.localStorage.getItem(key(owner, dseq)) } catch { return null }
}

/*
  An update whose transaction was sent but not yet seen in a block. The chain
  may still record it, so the planned SDL is kept apart from the confirmed
  one until the chain's version says which of the two is running
  (`signedSdlFor` in core/instanceUpdate promotes it).
*/
function pendingKey(owner: string, dseq: bigint | number | string): string {
  return `ddws.console.deployed-sdl.pending.${owner}.${dseq.toString()}`
}

/** Keep `sdl` as the update `owner` sent for `dseq`, until the chain confirms or refuses it. */
export function rememberPendingSdl(owner: string, dseq: bigint | number | string, sdl: string): void {
  try { window.localStorage.setItem(pendingKey(owner, dseq), sdl) } catch { /* storage unavailable: nothing to recover from */ }
}

/** The update `owner` sent for `dseq` and the chain has not answered yet, or null. */
export function pendingSdl(owner: string, dseq: bigint | number | string): string | null {
  try { return window.localStorage.getItem(pendingKey(owner, dseq)) } catch { return null }
}

/** Forget the pending update for `dseq`. */
export function clearPendingSdl(owner: string, dseq: bigint | number | string): void {
  try { window.localStorage.removeItem(pendingKey(owner, dseq)) } catch { /* nothing to clear */ }
}
